import { toast } from '@nexus/ui';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAdminError } from '../admin/shared';
import { api } from '../api/client';
import { personalChannelsQuery } from './channels-api';
import { deviceKind, PushSetupError, subscribePush } from './push';

/**
 * Switches push on for this device: asks for permission, subscribes the browser and registers it
 * with the server. Not a mutation, because Safari asks only straight from the click and a mutation
 * starts its function a moment later.
 */
export function usePushSwitch(): {
  switchOn: (publicKey: string) => Promise<boolean>;
  switching: boolean;
  known: Record<string, string>;
} {
  const { t } = useTranslation();
  const client = useQueryClient();
  const reportError = useAdminError();
  const [switching, setSwitching] = useState(false);
  const known = {
    'not-configured': t('profile.channels.push.errors.unavailable'),
    'no-address': t('profile.channels.push.errors.no-device'),
    'too-many-devices': t('profile.channels.push.errors.too-many-devices'),
    rejected: t('profile.channels.push.errors.rejected'),
    'connection-failed': t('profile.channels.push.errors.connection-failed'),
    'auth-failed': t('profile.channels.push.errors.rejected'),
  };

  function switchOn(publicKey: string): Promise<boolean> {
    const subscribing = subscribePush(publicKey);
    setSwitching(true);
    return subscribing
      .then((subscription) =>
        api.post('/notifications/channels/push', {
          endpoint: subscription.endpoint,
          keys: subscription.keys,
          device: deviceKind(),
        }),
      )
      .then(async () => {
        toast.success(t('profile.channels.push.switchedOn'));
        await client.invalidateQueries(personalChannelsQuery);
        return true;
      })
      .catch((error: unknown) => {
        if (error instanceof PushSetupError) {
          if (error.code !== 'dismissed') {
            toast.error(t(`profile.channels.push.errors.${error.code}`));
          }
        } else {
          reportError(error, known);
        }
        return false;
      })
      .finally(() => setSwitching(false));
  }

  return { switchOn, switching, known };
}
